import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';

import { CustomEventService } from '@shared/services/custom-event.service';
import { WidgetService } from '@shared/services/widget.service';

import { DeveloperListModalComponent } from '@shared/components/modals/developer-list-modal/developer-list-modal.component';

import ActionTypesEnum from '@shared/enums/action-constant.enum';
import { WidgetStatePlaceholderEnum } from '@shared/enums/widget-state-placeholder.enum';

import colorMap from '@shared/variables/chart-color.variable';
import { commonChartTooltip } from '@shared/variables/echart-common.variable';

import GenerateDataZoomUtil from '@shared/utils/data-zoom.util';
import { createImageUrl, fullNamePropertyCreator } from '@shared/utils/object-mapper.util';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EChartOption } from 'echarts';
import { fromJS, Map } from 'immutable';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';

@UntilDestroy()
@Component({
  selector: 'app-repository-stats-widget',
  templateUrl: './repository-stats-widget.component.html'
})
export class RepositoryStatsWidgetComponent implements OnInit {

  @Input() dateFilterable: boolean;
  widgetId: string;
  widgetName: string;
  widgetType: string;
  widgetFooter: Widget.FooterModel;
  headerAddon: string;
  headerComponents: [] = [];
  selectedDate: string;
  note: string;
  url: string;
  from: number;
  showDownloadIcon: boolean;

  @Input()
  set period(period: string) {
    if (period === 'FROM') {
      this.selectedDate = this.from.toString(10);
    } else if (period === 'TODAY') {
      this.selectedDate = `${period}`;
    } else if (period) {
      this.selectedDate = `FROM_LAST_${period}`;
    }
  };

  loading: boolean;
  initialLoading: boolean;
  widgetStatePlaceholderEnum = WidgetStatePlaceholderEnum;
  showWidgetStatePlaceholder: WidgetStatePlaceholderEnum;

  analysesDetailData: Map<string, any> = Map();
  modal: NzModalRef;
  chartOption: EChartOption;

  @ViewChild('toolTipTitle') toolTipTitle: TemplateRef<any>;
  toolTipHTML: string;

  constructor(private customEventService: CustomEventService,
              private widgetService: WidgetService,
              private modalService: NzModalService) { }

  ngOnInit() {
    this.toolTipHTML = this.headerComponents.join('<br>');

    this.loadWidgetData(true);

    // Subscribe to the dashboard announcements to update
    this.customEventService.eventAnnounced$.pipe(
      untilDestroyed(this)
    ).subscribe(({ action, data }) => {
      if (action === ActionTypesEnum.UPDATE_WIDGET_DATA) {
        this.loadWidgetData();
      }

      if (action === ActionTypesEnum.UPDATE_WIDGET_DATE) {
        const { id, date } = data;

        if (this.widgetId === id ) {
          this.selectedDate = date;
          this.loadWidgetData();
        }
      }
    });
  }

  loadWidgetData(initial?: boolean): void {
    this.loading = true;

    if (initial) {
      this.initialLoading = true;
    }

    this.widgetService.get(this.widgetId, this.selectedDate).pipe(
      untilDestroyed(this)
    ).subscribe((response) => {
      this.loading = false;
      this.initialLoading = false;

      if (!response.data) {
        this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_FOUND;
        return;
      }
      this.showWidgetStatePlaceholder = undefined;

      if (response.footer) {
        this.widgetFooter = response.footer;
      }

      this.analysesDetailData = fromJS(response);

      if (this.analysesDetailData.has('data')
        && this.analysesDetailData.get('data').size) {
        this.analysesDetailData = this.analysesDetailData
          .updateIn(['data', 'committer'], val => val
            .map(committer => committer
              .merge({
                backgroundImage: committer.get('avatar') ? `url(${createImageUrl(committer.get('avatar'))})` : null,
              })
            )
          );
      }
      this.loadChartData();
    }, error => {
      console.log('error occurred while fetching initial data for gitwiser analysis detail', error);
      this.loading = false;
      this.initialLoading = false;
      this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_FOUND;
    });
  }

  loadChartData(): void {
    const chartData = this.analysesDetailData.get('data');

    const commonTooltip: EChartOption.Tooltip = {
      ...commonChartTooltip,
      trigger: 'axis',
      formatter: (params: any[]) => {
        return params.reduce((acc, curr, index) => {
          if (index === 0) {
            acc = `${curr.axisValueLabel}<br>`;
          }

          acc = acc + `${curr.marker} ${curr.seriesName} : ${curr.data}<br>`;
          return acc;
        }, '');
      }
    };

    if (!chartData || !chartData.has('chart')) {
      return;
    }

    const data = chartData.get('chart');

    const tempColor = ['Deleted', 'Commits', 'Added'];

    const summarySeries = data.get('legend').map((item, index) => {
      let val = Map({
        name: item,
        type: index === 2 ? 'line' : 'bar',
        data: data.getIn(['series', index]).toJS(),
        itemStyle: {
          color: colorMap.get('gitwiser-summary')[tempColor.indexOf(item)]
        },
        barWidth: data.get('xAxis').size < 30 ? 20 : null,
        yAxisIndex: index === 2 ? 1 : 0
      });

      if (index !== 2) {
        val = val
          .merge({
            stack: 'commit'
          });
      }

      return val;
    }).toJS();

    const dataZoom = () => {
      if (data.get('xAxis').size >= 30) {
        return GenerateDataZoomUtil.generateDataZoom(
          {
            dataCount: data.get('xAxis').size,
            orient: 'horizontal',
            showDataShadow: true,
            left: 5,
            right: 5,
            height: 30,
            x: 30
          })
      } else { return [] }
    };

    this.chartOption = {
      dataZoom: dataZoom(),
      grid: {
        left: 0,
        right: 0,
        top: 5,
        bottom: 50
      },
      tooltip: commonTooltip,
      xAxis: [
        {
          show: false,
          type: 'category',
          data: data.get('xAxis').toJS()
        }
      ],
      yAxis: [
        {
          show: false,
          type: 'value',
        },
        {
          show: false,
          type: 'value',
        }
      ],
      series: summarySeries
    };
  }

  showDeveloperListModal(): void {
    this.modal = this.modalService.create({ 
      nzMaskClosable: false,
      nzContent: DeveloperListModalComponent,
      nzComponentParams: {
        developers: this.analysesDetailData.getIn(['data', 'committer']).toJS().map(fullNamePropertyCreator),
        type: 'contributor',
        title: 'Contributor List'
      },
      nzWidth: '450px',
      nzFooter: null
    });
  }

  onChartEvent($event: any): void {
    if (this.analysesDetailData.hasIn(['data', 'chart', 'urls'])
        && this.analysesDetailData.getIn(['data', 'chart', 'urls']).size) {
      const urls = this.analysesDetailData.getIn(['data', 'chart', 'urls']).toJS();

      const seriesNames = this.analysesDetailData.getIn(['data', 'chart', 'legend']).toJS();

      const seriesNameIndex = seriesNames.findIndex(item => item === $event.seriesName);

      if (seriesNameIndex > -1 && urls[seriesNameIndex] && urls[seriesNameIndex][$event.dataIndex]) {
        window.open(urls[seriesNameIndex][$event.dataIndex], '_blank');
      }
    }
  }
}
